import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ShoppingCart, Package, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({ items: [] });
  const [user, setUser] = useState(null);
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    const userData = localStorage.getItem('customer_user');
    if (!userData) {
      navigate('/customer/login');
      return;
    }
    setUser(JSON.parse(userData));
    fetchProducts();
    fetchCart();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/public`);
      setProducts(response.data);
      const initialQty = {};
      response.data.forEach(p => initialQty[p.id] = 1);
      setQuantities(initialQty);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const fetchCart = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.get(`${API}/customer/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to load cart');
    }
  };

  const addToCart = async (product) => {
    try {
      const token = localStorage.getItem('customer_token');
      const quantity = quantities[product.id] || 1;
      
      await axios.post(`${API}/customer/cart/add`, 
        { product_id: product.id, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Added ${product.name} to cart`);
      fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    navigate('/customer/login');
  };

  const getPrice = (product) => {
    const qty = quantities[product.id] || 1;
    const customerType = user?.customer_type || 'retail';
    
    // COLORED CHALK PRICING
    if (product.category === 'colored') {
      if (customerType === 'retail' || (customerType === 'wholesale' && qty <= 20)) {
        return qty <= 20 ? 28000 : 27000;
      }
      
      if (customerType === 'wholesale') {
        if (qty >= 51) return 26000;
        if (qty >= 21) return 27000;
        return 28000;
      }
      
      return 35000; // default retail
    }
    
    // WHITE CHALK PRICING
    if (customerType === 'retail' || (customerType === 'wholesale' && qty < 20)) {
      return 28000;
    }
    
    if (customerType === 'wholesale' && qty >= 20) {
      if (qty >= 151) return 20000;
      if (qty >= 101) return 21000;
      if (qty >= 51) return 21500;
      return 22000;
    }
    
    return 28000;
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/himan-logo.png" alt="Himan Logo" className="h-12 w-12" />
            <div>
              <h1 className="text-xl font-bold text-[#010101]">Himan Investment & Company</h1>
              <p className="text-sm text-gray-600">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/customer/orders')}
              className="gap-2"
            >
              <Package size={18} />
              My Orders
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/customer/cart')}
              className="gap-2 relative"
              data-testid="cart-button"
            >
              <ShoppingCart size={18} />
              Cart
              {cart?.items?.length > 0 && (
                <Badge className="absolute -top-2 -right-2 bg-[#EA5829] text-white">
                  {cart.items.length}
                </Badge>
              )}
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="gap-2">
              <LogOut size={18} />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-[#010101]">Product Catalog</h2>
          <p className="text-gray-600">Browse our quality chalk products</p>
          <div className="mt-2">
            <Badge variant="outline" className="text-[#8FC043]">
              Account Type: {user?.customer_type?.toUpperCase()}
            </Badge>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-12">
            <Package size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No products available at the moment</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card key={product.id} data-testid={`product-${product.id}`} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="aspect-square bg-gray-100 rounded-md mb-4 flex items-center justify-center">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-full h-full object-cover rounded-md" />
                    ) : (
                      <Package size={64} className="text-gray-400" />
                    )}
                  </div>
                  <CardTitle className="text-lg">{product.name}</CardTitle>
                  <p className="text-sm text-gray-600">{product.description}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline">{product.category}</Badge>
                    <Badge variant="outline">{product.product_type}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Stock Available</p>
                      <p className="text-lg font-semibold text-[#8FC043]">{product.stock_quantity} units</p>
                    </div>
                    <div>
                      <Label htmlFor={`qty-${product.id}`} className="text-sm">Quantity</Label>
                      <Input
                        id={`qty-${product.id}`}
                        type="number"
                        min="1"
                        max={product.stock_quantity}
                        value={quantities[product.id] || 1}
                        onChange={(e) => setQuantities({...quantities, [product.id]: parseInt(e.target.value) || 1})}
                        className="mt-1"
                      />
                    </div>
                    <div className="border-t pt-4">
                      <p className="text-sm text-gray-600">Price per unit</p>
                      <p className="text-2xl font-bold text-[#EA5829]">Tsh {getPrice(product).toLocaleString()}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Total: Tsh {(getPrice(product) * (quantities[product.id] || 1)).toLocaleString()}
                      </p>
                      {user?.customer_type === 'wholesale' && quantities[product.id] >= 20 && (
                        <Badge className="mt-2 bg-[#8FC043] text-white">Wholesale Price Applied!</Badge>
                      )}
                    </div>
                    <Button
                      onClick={() => addToCart(product)}
                      className="w-full"
                      disabled={product.stock_quantity === 0}
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <ShoppingCart size={16} className="mr-2" />
                      {product.stock_quantity === 0 ? 'Out of Stock' : 'Add to Cart'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
